import { Observable, of } from 'rxjs'
import { AppState } from 'src/app/core/core.state'
import { environment } from 'src/environments/environment'

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { Router } from '@angular/router'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { catchError, take, timeout } from 'rxjs/operators'
import { NotificationService } from 'src/app/core/notification.service'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { Location } from 'src/app/features/location/location'
import { locationSelectors } from 'src/app/features/location/location.selectors'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { Member } from '../../../member/member'
import { memberActions } from '../../../member/member.actions'
import { memberSelectors } from '../../../member/member.selectors'
import { Organization } from '../../../organization/organization'
import { organizationActions } from '../../../organization/organization.actions'
import { organizationSelectors } from '../../../organization/organization.selectors'
import { Product } from '../../../product/product'
import { productActions } from '../../../product/product.actions'
import { productSelectors } from '../../../product/product.selectors'
import {
  SerieProposal,
  SerieProposalStatus,
  SerieProposalType
} from '../serie-proposal'
import { SerieProposalEditModalComponent } from '../serie-proposal-edit-modal/show.component'
import {
  SerieProposalItem,
  SerieProposalItemStatus
} from '../serie-proposal-item/serie-proposal-item'
import { serieProposalItemActions } from '../serie-proposal-item/serie-proposal-item.actions'
import { serieProposalItemSelectors } from '../serie-proposal-item/serie-proposal-item.selectors'
import { SerieProposalSeriesModalComponent } from '../serie-proposal-series-modal/serie-proposal-series-modal.component'
import { serieProposalActions } from '../serie-proposal.actions'
import { serieProposalSelectors } from '../serie-proposal.selectors'
import { SerieProposalService } from '../serie-proposal.service'
import { SerieProposalItemShowModalComponent } from '../serie-proposal-series-item-modal/show-modal.component'
import { TranslateService } from '@ngx-translate/core'

@UntilDestroy()
@Component({
  selector: 'serie-proposal-form-general',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss']
})
export class SerieProposalFormGeneralComponent implements OnInit {
  @Output()
  onCloseModal = new EventEmitter<any>()
  @Input()
  serieProposal: SerieProposal
  @Input() isFromModal: boolean = false

  public form: UntypedFormGroup
  public initialFormValue: any
  public products$: Observable<Product[]>
  public moduleConfigs: any = environment.moduleConfigs
  public accessRights = AccessRights
  public isLoading: boolean
  public isLoading$: Observable<boolean>
  public memberEntities$: Observable<Dictionary<Member>>
  public serieProposalItemEntities$: Observable<Dictionary<SerieProposalItem>>
  public productEntities$: Observable<Dictionary<Product>>
  public locationEntities$: Observable<Dictionary<Location>>
  public organizations$: Observable<Organization[]>
  public serieProposalItems$: Observable<SerieProposalItem[]>
  public serieProposalItem: SerieProposalItem
  public organizationEntities$: Observable<Dictionary<Organization>>
  public daysOfWeek: number[] = [1, 2, 3, 4, 5, 6, 0]
  public selectedLanguage$: Observable<string>
  public serieProposalType = SerieProposalType
  public serieProposalStatus = SerieProposalStatus
  public serieProposalItemStatus = SerieProposalStatus
  public bsModalRef: BsModalRef

  get selectedDays(): number[] {
    return this.serieProposal.details.repeatableBookings.map(
      (booking) => booking.dayOfWeek
    )
  }

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private actions$: Actions,
    private notificationService: NotificationService,
    private router: Router,
    private modalService: BsModalService,
    private serieProposalService: SerieProposalService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      days: this.fb.array([]),
      linkedProductId: [null, []],
      productId: [null, []],
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      status: [null, [Validators.required]],
      internalMemo: [null, []],
      externalMemo: [null, []],
      organizationId: [null, []],
      customerPersonId: [null, [Validators.required]],
      groupInformation: [null]
    })

    this.isLoading$ = this.store.select(serieProposalSelectors.selectIsLoading)
    this.isLoading = false

    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.memberEntities$ = this.store.select(memberSelectors.selectEntities)
    this.serieProposalItemEntities$ = this.store.select(
      serieProposalItemSelectors.selectEntities
    )
    this.organizationEntities$ = this.store.select(
      organizationSelectors.selectEntities
    )

    this.productEntities$ = this.store.select(productSelectors.selectEntities)

    this.locationEntities$ = this.store.select(locationSelectors.selectEntities)

    this.serieProposalItems$ = this.store.select(
      serieProposalItemSelectors.selectByFilter,
      {
        field: 'serieProposalId',
        value: this.serieProposal.id
      }
    )

    this.serieProposalItemEntities$
      .pipe(take(1))
      .subscribe((serieProposalItemEntities) => {
        const linkedProductId =
          serieProposalItemEntities[this.serieProposal.serieProposalItems?.[0]]
            ?.linkedProductId

        this.form.controls.linkedProductId.setValue(linkedProductId)

        const productId =
          serieProposalItemEntities[this.serieProposal.serieProposalItems?.[0]]
            ?.productId

        if (productId) {
          this.form.controls.productId.setValue(productId)
        }
      })

    this.form.controls.internalMemo.setValue(this.serieProposal?.internalMemo)
    setTimeout(() => {
      this.setInitialFomValues()
    }, 500)
  }
  private setInitialFomValues() {
    this.initialFormValue = this.form.value
  }
  private isOnlyFieldChanged(fieldName: string): boolean {
    const currentFormValue = this.form.value
    const initialFormValue = this.initialFormValue

    for (let key in currentFormValue) {
      if (
        key !== fieldName &&
        currentFormValue[key] !== initialFormValue[key]
      ) {
        return false
      }
    }
    return currentFormValue[fieldName] !== initialFormValue[fieldName]
  }
  public onSearch(term: string, type: string) {
    switch (type) {
      case 'product':
        this.store.dispatch(
          productActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                description: { [CondOperator.CONTAINS_LOW]: term }
              }
            })
          })
        )
        break
      case 'organization':
        this.store.dispatch(
          organizationActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                description: { [CondOperator.CONTAINS_LOW]: term }
              }
            })
          })
        )
        break
      case 'member':
        this.store.dispatch(
          memberActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                $or: [
                  {
                    firstName: {
                      [CondOperator.CONTAINS_LOW]: term
                    }
                  },
                  {
                    email: {
                      [CondOperator.CONTAINS_LOW]: term
                    }
                  },
                  {
                    lastName: {
                      [CondOperator.CONTAINS_LOW]: term
                    }
                  }
                ]
              }
            })
          })
        )
        break
    }
  }
  public updateinternalMemo() {
    if (!this.serieProposal.id) return
    this.store.dispatch(
      serieProposalActions.updateOneById({
        id: this.serieProposal.id,
        params: {
          internalMemo: this.form.controls['internalMemo'].value
        }
      })
    )
    this.actions$
      .pipe(
        ofType(serieProposalActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => {
          this.isLoading = false
          return of(null)
        })
      )
      .subscribe((action) => {
        if (action) {
          this.notificationService.emit('alerts.success', 'success')
        }
      })
  }
  public isDaySelected(day: number): boolean {
    const mappedDay = day == 0 ? 7 : day
    return this.selectedDays.includes(mappedDay)
  }
  public onSerieProposalTypeclick(type) {
    // this.selectedSerieProposalType = type
  }
  public getTimesForDay(dayNumber: number): {
    startTime: string
    endTime: string
  } {
    if (!this.serieProposal.details.repeatableBookings?.length) {
      return { startTime: null, endTime: null }
    }
    const repeatableBooking =
      this.serieProposal.details.repeatableBookings.find(
        (booking: { dayOfWeek: number }) => booking.dayOfWeek === dayNumber
      )

    if (repeatableBooking) {
      return {
        startTime: repeatableBooking.startTime.slice(0, 5),
        endTime: repeatableBooking.endTime.slice(0, 5)
      }
    } else {
      return { startTime: null, endTime: null }
    }
  }

  public onClickEdit() {
    const selectedRepeatableBooking =
      this.serieProposal.details.repeatableBookings?.[0]

    if (selectedRepeatableBooking) {
      this.bsModalRef = this.modalService.show(
        SerieProposalEditModalComponent,
        {
          initialState: {
            serieProposal: this.serieProposal,
            repeatableBooking: selectedRepeatableBooking
          },
          class: 'modal-md modal-dialog-scrollable'
        }
      )
    }
  }

  public onClickEditItem(serieProposalItemId: number) {
    this.serieProposalItemEntities$
      .pipe(take(1))
      .subscribe((serieProposalItemEntities) => {
        this.bsModalRef = this.modalService.show(
          SerieProposalItemShowModalComponent,
          {
            initialState: {
              serieProposal: this.serieProposal,
              serieProposalItem: {
                ...serieProposalItemEntities[serieProposalItemId],
                startDate: moment(
                  serieProposalItemEntities[serieProposalItemId].startDate
                ),
                endDate: moment(
                  serieProposalItemEntities[serieProposalItemId].endDate
                )
              }
            },
            class: 'modal-md modal-dialog-scrollable'
          }
        )
      })
  }

  public onClickDecline(serieItemId?: number) {
    const message = this.translate.instant(
      'serieProposalModule.declineConfirmation'
    )
    if (!window.confirm(message)) {
      return
    }

    if (serieItemId) {
      this.store.dispatch(
        serieProposalItemActions.updateOneById({
          id: serieItemId,
          params: {
            status: SerieProposalItemStatus.DECLINED
          }
        })
      )

      this.actions$
        .pipe(
          ofType(serieProposalItemActions.upsertMany),
          take(1),
          timeout(5000),
          catchError((error) => of(null))
        )
        .subscribe((action) => {
          if (action) {
            this.notificationService.emit('alerts.success', 'success')
          }
        })
    } else {
      this.store.dispatch(
        serieProposalActions.updateOneById({
          id: this.serieProposal.id,
          params: {
            status: this.serieProposalStatus.DECLINED
          }
        })
      )

      this.actions$
        .pipe(
          ofType(serieProposalActions.upsertMany),
          take(1),
          timeout(5000),
          catchError((error) => of(null))
        )
        .subscribe((action) => {
          if (action) {
            this.declineSerieProposalItems()

            this.notificationService.emit('alerts.success', 'success')
          }
        })
    }

    if (this.isFromModal) {
      this.onCloseModal.emit()
    } else if (!serieItemId) {
      this.router.navigate([`/bookings`], {
        queryParams: { selectedType: 'serieProposals' }
      })
    }
  }

  public onClickAccept() {
    if (this.isOnlyFieldChanged('internalMemo')) {
      this.store.dispatch(
        serieProposalActions.updateOneById({
          id: this.serieProposal.id,
          params: {
            internalMemo: this.form.controls['internalMemo'].value
          }
        })
      )
      this.actions$
        .pipe(
          ofType(serieProposalActions.upsertMany),
          take(1),
          timeout(5000),
          catchError((error) => {
            this.isLoading = false
            return of(null)
          })
        )
        .subscribe((action) => {
          if (action) {
            this.notificationService.emit('alerts.success', 'success')

            if (this.isFromModal) {
              this.onCloseModal.emit()
            }
          }
        })
    } else {
      this.serieProposalService
        .getConflictingBookingsBySerieProposalId$(this.serieProposal.id)
        .pipe(take(1))
        .subscribe((response) => {
          if (!response) {
            return
          }

          if (response.serieProposalItemsWithConflicts?.length) {
            const bsModalRef = this.modalService.show(
              SerieProposalSeriesModalComponent,
              {
                initialState: {
                  serieProposalItemsWithoutConflicts:
                    response.serieProposalItemsWithoutConflicts,
                  serieProposalItemsWithConflicts:
                    response.serieProposalItemsWithConflicts,
                  internalMemo: this.form.controls['internalMemo'].value
                },
                class:
                  'modal-xl modal-dialog-centered modal-dialog-scrollable ',
                backdrop: 'static'
              }
            )
          } else {
            this.store.dispatch(
              serieProposalActions.updateOneById({
                id: this.serieProposal.id,
                params: {
                  status: SerieProposalStatus.ACCEPTED,
                  internalMemo: this.form.controls['internalMemo'].value
                }
              })
            )

            this.actions$
              .pipe(
                ofType(serieProposalActions.upsertMany),
                take(1),
                timeout(5000),
                catchError((error) => {
                  this.isLoading = false
                  return of(null)
                })
              )
              .subscribe((action) => {
                if (action) {
                  this.notificationService.emit('alerts.success', 'success')

                  if (this.isFromModal) {
                    this.onCloseModal.emit()
                  }
                }
              })
          }
        })
    }
  }

  public showEditButton(): boolean {
    return (
      this.serieProposal.status != SerieProposalStatus.DECLINED &&
      this.serieProposal.details.repeatableBookings?.length >= 1
    )
  }

  private declineProposalOfSelectedDay(day: number) {
    const selectedRepeatableBooking =
      this.serieProposal.details.repeatableBookings.find(
        (booking) => booking.dayOfWeek === day
      )

    if (!selectedRepeatableBooking) {
      console.error('No repeatable booking found for the selected day.')
      return
    }

    const newRepeatableBookingsToSave =
      this.serieProposal.details.repeatableBookings.filter(
        (booking) => booking.dayOfWeek != day
      )

    this.store.dispatch(
      serieProposalActions.updateOneById({
        id: this.serieProposal.id,
        params: {
          details: {
            ...this.serieProposal.details,
            repeatableBookings: newRepeatableBookingsToSave
          }
        }
      })
    )

    this.actions$
      .pipe(
        ofType(serieProposalActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (action) {
          this.declineSerieProposalItems(selectedRepeatableBooking)

          this.notificationService.emit('alerts.success', 'success')
        }
      })
  }

  private declineSerieProposalItems(repeatableBooking?) {
    this.serieProposalItems$.pipe(take(1)).subscribe((serieProposalItems) => {
      if (!repeatableBooking) {
        serieProposalItems.forEach((item) => {
          this.store.dispatch(
            serieProposalItemActions.updateOneById({
              id: item.id,
              params: {
                status: SerieProposalItemStatus.DECLINED
              }
            })
          )
        })
      } else {
        const itemsToDecline = serieProposalItems.filter((item) => {
          return (
            moment(item.startDate).day() == repeatableBooking.dayOfWeek &&
            moment(item.startDate).format('HH:mm:ss') ==
              repeatableBooking.startTime
          )
        })

        itemsToDecline.forEach((item) => {
          this.store.dispatch(
            serieProposalItemActions.updateOneById({
              id: item.id,
              params: {
                status: SerieProposalItemStatus.DECLINED
              }
            })
          )
        })
      }
    })

    this.actions$
      .pipe(
        ofType(serieProposalItemActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (action) {
          this.notificationService.emit('alerts.success', 'success')
          // todo ? set serie proposal to declined
        }
      })
  }
}
